import { useEffect, useState, useRef } from "react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Modal, Field, inputClass, PrimaryButton, EmptyState } from "../components/common";
import { toast } from "sonner";
import { Compass, Plus, Trash, MapPin, Images, ArrowsLeftRight, UploadSimple } from "@phosphor-icons/react";

function usePannellum() {
  const [ready, setReady] = useState(!!window.pannellum);
  useEffect(() => {
    if (window.pannellum) { setReady(true); return; }
    if (!document.getElementById("pnlm-css")) {
      const css = document.createElement("link"); css.id = "pnlm-css"; css.rel = "stylesheet";
      css.href = "https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.css"; document.head.appendChild(css);
    }
    if (!document.getElementById("pnlm-js")) {
      const s = document.createElement("script"); s.id = "pnlm-js";
      s.src = "https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.js";
      s.onload = () => setReady(true); document.body.appendChild(s);
    } else { document.getElementById("pnlm-js").addEventListener("load", () => setReady(true)); }
  }, []);
  return ready;
}

function Pano360({ url }) {
  const ref = useRef(null);
  const ready = usePannellum();
  useEffect(() => {
    if (!ready || !ref.current || !url) return;
    let viewer;
    ref.current.innerHTML = "";
    try { viewer = window.pannellum.viewer(ref.current, { type: "equirectangular", panorama: url, autoLoad: true, showZoomCtrl: true, showFullscreenCtrl: true }); }
    catch (e) { /* noop */ }
    return () => { try { viewer && viewer.destroy(); } catch (e) {} };
  }, [ready, url]);
  return <div ref={ref} className="w-full h-80 bg-black rounded-sm" data-testid="pano-viewer">{!ready && <div className="w-full h-full flex items-center justify-center text-white text-sm">Cargando visor 360°…</div>}</div>;
}

const PhotoView = ({ photo }) => {
  if (!photo) return <div className="w-full h-80 bg-muted rounded-sm flex items-center justify-center text-sm text-muted-foreground">Sin foto para esta fecha</div>;
  return photo.is_360
    ? <Pano360 url={fileUrl(photo.storage_path)} />
    : <img src={fileUrl(photo.storage_path)} alt="captura" className="w-full h-80 object-contain bg-black rounded-sm" />;
};

export default function Recorridos360() {
  const { user } = useAuth();
  const canManage = true;
  const canDelete = ["admin", "gerente_general", "gerente_area"].includes(user?.role) || !!user?.can_delete;
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState("");
  const [points, setPoints] = useState([]);
  const [addMode, setAddMode] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [planForm, setPlanForm] = useState({ name: "", level: "" });
  const [planFile, setPlanFile] = useState(null);
  const [planBusy, setPlanBusy] = useState(false);
  const [sel, setSel] = useState(null); // selected point
  const [dateA, setDateA] = useState("");
  const [dateB, setDateB] = useState("");
  const [compare, setCompare] = useState(false);
  const [upForm, setUpForm] = useState({ date: new Date().toISOString().slice(0, 10), is_360: true });
  const [upFile, setUpFile] = useState(null);
  const [upBusy, setUpBusy] = useState(false);

  const loadPlans = () => api.get("/capture-plans").then((r) => { setPlans(r.data); if (!planId && r.data[0]) setPlanId(r.data[0].id); }).catch(() => {});
  const loadPoints = (pid) => api.get(`/capture-plans/${pid || planId}/points`).then((r) => setPoints(r.data)).catch(() => {});
  useEffect(() => { loadPlans(); }, []);
  useEffect(() => { if (planId) loadPoints(planId); }, [planId]);

  const plan = plans.find((p) => p.id === planId);

  const savePlan = async (e) => {
    e.preventDefault();
    if (!planFile) return toast.error("Selecciona la imagen del plano");
    setPlanBusy(true);
    const fd = new FormData(); fd.append("name", planForm.name); fd.append("level", planForm.level); fd.append("file", planFile);
    try { const r = await api.post("/capture-plans", fd); toast.success("Plano agregado"); setPlanOpen(false); setPlanForm({ name: "", level: "" }); setPlanFile(null); await loadPlans(); setPlanId(r.data.id); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPlanBusy(false); }
  };
  const delPlan = async () => { if (!plan) return; if (!window.confirm(`¿Eliminar el plano "${plan.name}" y todos sus puntos?`)) return; try { await api.delete(`/capture-plans/${plan.id}`); toast.success("Plano eliminado"); setPlanId(""); await loadPlans(); setPoints([]); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const onPlanClick = async (e) => {
    if (!addMode) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    const name = window.prompt("Nombre del punto (ej. Acceso, Lobby, Eje A - N1):");
    if (!name || !name.trim()) return;
    try { await api.post("/capture-points", { plan_id: planId, name: name.trim(), x, y }); toast.success("Punto agregado"); loadPoints(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openPoint = (pt) => {
    setSel(pt); setCompare(false);
    const dates = [...new Set((pt.photos || []).map((p) => p.date))].sort().reverse();
    setDateA(dates[0] || ""); setDateB(dates[1] || dates[0] || "");
    setUpForm({ date: new Date().toISOString().slice(0, 10), is_360: true }); setUpFile(null);
  };
  const delPoint = async (cid) => { if (!window.confirm("¿Eliminar este punto y sus fotos?")) return; try { await api.delete(`/capture-points/${cid}`); setSel(null); loadPoints(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const uploadPhoto = async (e) => {
    e.preventDefault();
    if (!upFile) return toast.error("Selecciona una imagen");
    setUpBusy(true);
    const fd = new FormData(); fd.append("date", upForm.date); fd.append("is_360", String(upForm.is_360)); fd.append("file", upFile);
    try {
      await api.post(`/capture-points/${sel.id}/photos`, fd);
      toast.success("Foto agregada");
      const r = await api.get(`/capture-plans/${planId}/points`); setPoints(r.data);
      const fresh = r.data.find((p) => p.id === sel.id); setSel(fresh);
      const dates = [...new Set((fresh.photos || []).map((p) => p.date))].sort().reverse();
      setDateA(upForm.date); setDateB(dates[1] || dates[0] || ""); setUpFile(null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUpBusy(false); }
  };
  const delPhoto = async (photoId) => { try { await api.delete(`/capture-points/${sel.id}/photos/${photoId}`); const r = await api.get(`/capture-plans/${planId}/points`); setPoints(r.data); setSel(r.data.find((p) => p.id === sel.id)); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const selDates = sel ? [...new Set((sel.photos || []).map((p) => p.date))].sort().reverse() : [];
  const photoFor = (d) => { const list = (sel?.photos || []).filter((p) => p.date === d); return list[list.length - 1] || null; };

  return (
    <div>
      <PageHeader overline="Documentación visual de obra" title="Recorridos 360°"
        action={<div className="flex items-center gap-2">
          {plan && canManage && (
            <button onClick={() => setAddMode((v) => !v)} data-testid="toggle-addpoint-btn"
              className={`inline-flex items-center gap-2 h-11 px-4 rounded-sm font-medium text-sm border transition-colors duration-200 ${addMode ? "bg-primary text-white border-primary" : "border-input hover:bg-muted"}`}>
              <MapPin size={18} weight="bold" /> {addMode ? "Clic en el plano para ubicar…" : "Agregar punto"}
            </button>
          )}
          <PrimaryButton onClick={() => setPlanOpen(true)} data-testid="add-plan-btn"><Plus size={18} weight="bold" /> Nuevo plano</PrimaryButton>
        </div>} />

      <Card className="p-4 mb-6 text-sm text-muted-foreground border-l-4 border-l-primary">
        Sube el <b className="text-foreground">plano</b> de un nivel/área, coloca <b className="text-foreground">puntos</b> haciendo clic donde tomaste cada foto y sube <b className="text-foreground">fotos 360° o normales</b> con su fecha. Podrás <b className="text-foreground">comparar el avance</b> del mismo punto entre dos fechas.
      </Card>

      {plans.length === 0 ? (
        <EmptyState icon={Compass} title="Sin recorridos" subtitle="Agrega un plano para empezar a documentar la obra con fotos ubicadas."
          action={<PrimaryButton onClick={() => setPlanOpen(true)}><Plus size={18} weight="bold" /> Nuevo plano</PrimaryButton>} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <select data-testid="plan-select" className="h-10 px-3 rounded-sm border border-input bg-white text-sm min-w-[220px]" value={planId} onChange={(e) => { setPlanId(e.target.value); setAddMode(false); }}>
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name}{p.level ? ` · ${p.level}` : ""}</option>)}
            </select>
            {plan && canDelete && <button onClick={delPlan} data-testid="delete-plan-btn" className="text-sm text-danger hover:underline inline-flex items-center gap-1"><Trash size={15} /> Eliminar plano</button>}
            <span className="text-xs text-muted-foreground ml-auto">{points.length} punto(s)</span>
          </div>

          {plan && (
            <Card className="p-2 overflow-hidden">
              <div className={`relative w-full ${addMode ? "cursor-crosshair ring-2 ring-primary" : ""}`} onClick={onPlanClick} data-testid="plan-canvas">
                <img src={fileUrl(plan.plan_image_path)} alt={plan.name} className="w-full select-none pointer-events-none" draggable={false} />
                {points.map((pt, i) => (
                  <button key={pt.id} data-testid={`plan-pin-${pt.id}`}
                    onClick={(e) => { e.stopPropagation(); openPoint(pt); }}
                    title={pt.name}
                    style={{ left: `${pt.x}%`, top: `${pt.y}%` }}
                    className="absolute -translate-x-1/2 -translate-y-1/2 group">
                    <span className={`flex items-center justify-center w-7 h-7 rounded-full text-white text-xs font-bold shadow-lg ring-2 ring-white ${(pt.photos || []).length ? "bg-primary" : "bg-slate-400"}`}>{i + 1}</span>
                    <span className="absolute left-1/2 -translate-x-1/2 top-8 whitespace-nowrap bg-slate-900 text-white text-[10px] px-1.5 py-0.5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">{pt.name} ({(pt.photos || []).length})</span>
                  </button>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {/* Nuevo plano */}
      <Modal open={planOpen} onClose={() => setPlanOpen(false)} title="Nuevo plano / nivel">
        <form onSubmit={savePlan} className="space-y-4">
          <Field label="Nombre *"><input required data-testid="plan-name" className={inputClass} value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} placeholder="Ej. Planta baja, Torre A - Nivel 2" /></Field>
          <Field label="Nivel / área (opcional)"><input data-testid="plan-level" className={inputClass} value={planForm.level} onChange={(e) => setPlanForm({ ...planForm, level: e.target.value })} placeholder="Ej. N1, Sótano, Estacionamiento" /></Field>
          <Field label="Imagen del plano *">
            <input data-testid="plan-file" type="file" accept="image/*" onChange={(e) => setPlanFile(e.target.files[0])}
              className="w-full text-sm border border-dashed border-input rounded-sm p-3 file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-primary file:text-white file:rounded-sm file:font-medium" />
          </Field>
          <PrimaryButton type="submit" disabled={planBusy} data-testid="save-plan-btn">{planBusy ? "Subiendo…" : "Agregar plano"}</PrimaryButton>
        </form>
      </Modal>

      {/* Punto: fotos + comparar */}
      <Modal open={!!sel} onClose={() => setSel(null)} title={`Punto · ${sel?.name || ""}`}>
        {sel && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              {selDates.length >= 2 && (
                <button onClick={() => setCompare((v) => !v)} data-testid="compare-toggle" className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-sm border transition-colors ${compare ? "bg-primary text-white border-primary" : "border-input hover:bg-muted"}`}>
                  <ArrowsLeftRight size={15} weight="bold" /> {compare ? "Vista única" : "Comparar fechas"}
                </button>
              )}
              {canDelete && <button onClick={() => delPoint(sel.id)} className="ml-auto text-sm text-danger hover:underline inline-flex items-center gap-1"><Trash size={15} /> Eliminar punto</button>}
            </div>

            {selDates.length === 0 ? (
              <div className="w-full h-80 bg-muted rounded-sm flex items-center justify-center text-sm text-muted-foreground">Aún no hay fotos en este punto</div>
            ) : compare ? (
              <div className="grid grid-cols-2 gap-3">
                {["a", "b"].map((side) => (
                  <div key={side}>
                    <select data-testid={`compare-date-${side}`} className={inputClass + " mb-2"} value={side === "a" ? dateA : dateB} onChange={(e) => side === "a" ? setDateA(e.target.value) : setDateB(e.target.value)}>
                      {selDates.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <PhotoView photo={photoFor(side === "a" ? dateA : dateB)} />
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <select data-testid="date-select" className={inputClass + " mb-2"} value={dateA} onChange={(e) => setDateA(e.target.value)}>
                  {selDates.map((d) => <option key={d} value={d}>{d}{(sel.photos || []).some((p) => p.date === d && p.is_360) ? " · 360°" : ""}</option>)}
                </select>
                <PhotoView photo={photoFor(dateA)} />
                <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                  <span>{photoFor(dateA)?.is_360 ? "Arrastra para girar la vista 360°" : "Foto normal"}</span>
                  {canDelete && photoFor(dateA) && <button onClick={() => delPhoto(photoFor(dateA).id)} className="text-danger hover:underline inline-flex items-center gap-1"><Trash size={13} /> Eliminar esta foto</button>}
                </div>
              </div>
            )}

            <form onSubmit={uploadPhoto} className="bg-muted/40 p-3 rounded-sm space-y-3">
              <div className="text-sm font-semibold flex items-center gap-1.5"><UploadSimple size={16} weight="bold" /> Agregar foto</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Fecha"><input type="date" data-testid="photo-date" className={inputClass} value={upForm.date} onChange={(e) => setUpForm({ ...upForm, date: e.target.value })} /></Field>
                <label className="flex items-center gap-2 text-sm mt-6"><input type="checkbox" data-testid="photo-is360" checked={upForm.is_360} onChange={(e) => setUpForm({ ...upForm, is_360: e.target.checked })} /> Foto 360° (panorámica)</label>
              </div>
              <input data-testid="photo-file" type="file" accept="image/*" onChange={(e) => setUpFile(e.target.files[0])}
                className="w-full text-sm border border-dashed border-input rounded-sm p-2.5 file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-primary file:text-white file:rounded-sm file:font-medium" />
              <PrimaryButton type="submit" disabled={upBusy} data-testid="save-photo-btn"><Images size={16} weight="bold" /> {upBusy ? "Subiendo…" : "Subir foto"}</PrimaryButton>
            </form>
          </div>
        )}
      </Modal>
    </div>
  );
}
